import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $insertNodes,
  type BaseSelection,
  type ElementNode,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createQuoteNode } from '@lexical/rich-text';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { $createCodeNode } from '@lexical/code-core';
import { $createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { isAllowedLinkHref } from '@atlas/shared';
import {
  TeamsBoldIcon,
  TeamsBulletListIcon,
  TeamsCodeBlockIcon,
  TeamsComposeExpandIcon,
  TeamsItalicIcon,
  TeamsLinkIcon,
  TeamsNumberedListIcon,
  TeamsQuoteIcon,
  TeamsStrikethroughIcon,
  TeamsUnderlineIcon,
} from '@atlas/ui';

export interface TeamsComposerToolbarProps {
  disabled?: boolean;
  /** Expanded compose mode: taller box, and Enter no longer sends. */
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

/**
 * Formatting controls for the Teams composer.
 *
 * The control set is Teams' own, in Teams' order — this is an embedded Teams
 * surface, so anything ATLAS invents here reads as a bug to the user. Nothing
 * is offered that the wire model cannot carry: there are no headings, no font
 * sizes and no colours, because `TeamsBlockNode` has nowhere to put them and a
 * button that silently loses its formatting on send is worse than no button.
 *
 * Every control reflects the current selection (`aria-pressed`), recomputed on
 * each selection change, so the user can see what they are typing into.
 */
export function TeamsComposerToolbar({
  disabled = false,
  isExpanded = false,
  onToggleExpanded,
}: TeamsComposerToolbarProps) {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrike, setIsStrike] = useState(false);
  const [isLink, setIsLink] = useState(false);
  /** paragraph | quote | code | bullet | number */
  const [activeBlock, setActiveBlock] = useState('paragraph');

  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  /**
   * The selection as it was when the link popover opened.
   *
   * Typing a URL moves DOM focus out of the contenteditable. Lexical keeps its
   * own selection, but a click into the field can still collapse or clear it —
   * so the selection the link will apply to is captured up front and restored
   * before the edit, rather than hoped for.
   */
  const savedSelectionRef = useRef<BaseSelection | null>(null);

  const syncToolbarState = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    setIsBold(selection.hasFormat('bold'));
    setIsItalic(selection.hasFormat('italic'));
    setIsUnderline(selection.hasFormat('underline'));
    setIsStrike(selection.hasFormat('strikethrough'));

    const anchorNode = selection.anchor.getNode();
    setIsLink(
      Boolean($findMatchingParent(anchorNode, (parent) => $isLinkNode(parent))) ||
        $isLinkNode(anchorNode),
    );

    const element =
      anchorNode.getKey() === 'root'
        ? anchorNode
        : ($findMatchingParent(anchorNode, (candidate) => {
            const parent = candidate.getParent();
            return parent !== null && parent.getKey() === 'root';
          }) ?? anchorNode.getTopLevelElementOrThrow());

    if ($isListNode(element)) {
      const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
      setActiveBlock((parentList ?? element).getListType() === 'number' ? 'number' : 'bullet');
      return;
    }
    setActiveBlock(element.getType() === 'code' || element.getType() === 'quote'
      ? element.getType()
      : 'paragraph');
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(syncToolbarState);
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          syncToolbarState();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [editor, syncToolbarState]);

  /** Toggle a block type, falling back to a plain paragraph when already on it. */
  const setBlock = useCallback(
    (target: string, create: () => ElementNode) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        $setBlocksType(selection, activeBlock === target ? $createParagraphNode : create);
      });
    },
    [editor, activeBlock],
  );

  const toggleList = useCallback(
    (type: 'bullet' | 'number') => {
      if (activeBlock === type) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        return;
      }
      editor.dispatchCommand(
        type === 'bullet' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
        undefined,
      );
    },
    [activeBlock, editor],
  );

  const openLinkPopover = useCallback(
    (anchor: HTMLElement) => {
      // Already inside a link: the button removes it, matching Teams.
      if (isLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        return;
      }
      savedSelectionRef.current = editor.getEditorState().read(() => {
        const selection = $getSelection();
        return selection ? selection.clone() : null;
      });
      setLinkUrl('');
      setLinkError(null);
      setLinkAnchor(anchor);
    },
    [editor, isLink],
  );

  const applyLink = useCallback(() => {
    const url = linkUrl.trim();
    // Validated here AND in the serializer AND server-side. This gate exists to
    // tell the user, not to be the protection: a rejected scheme must never be
    // silently dropped, or they will believe a link was made.
    if (!isAllowedLinkHref(url)) {
      setLinkError(t('teams.panel.linkInvalid'));
      return;
    }

    const saved = savedSelectionRef.current;
    /**
     * Whether the link still needs wrapping around a live selection. Set inside
     * the update below, which Lexical runs synchronously.
     */
    let needsWrapping = false;

    const buildStandaloneLink = () => {
      // Nothing selected: the URL becomes its own link text, which is what
      // someone who clicked the button with no selection means.
      const link = $createLinkNode(url);
      link.append($createTextNode(url));
      return link;
    };

    editor.update(() => {
      if (saved) $setSelection(saved.clone());
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        // No caret anywhere — the button was used before the document was ever
        // focused. Append rather than drop the link on the floor.
        const paragraph = $createParagraphNode();
        paragraph.append(buildStandaloneLink());
        $getRoot().append(paragraph);
        return;
      }

      if (selection.isCollapsed()) {
        $insertNodes([buildStandaloneLink()]);
        return;
      }

      needsWrapping = true;
    });

    // Dispatched outside the update so Lexical's link plugin owns the wrapping
    // of an existing selection, including splitting partially-covered nodes.
    if (needsWrapping) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);

    setLinkAnchor(null);
    setLinkUrl('');
    setLinkError(null);
    editor.focus();
  }, [editor, linkUrl, t]);

  const buttons: ToolbarButton[] = [
    {
      key: 'bold',
      label: t('teams.panel.bold'),
      icon: <TeamsBoldIcon fontSize="small" />,
      selected: isBold,
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),
    },
    {
      key: 'italic',
      label: t('teams.panel.italic'),
      icon: <TeamsItalicIcon fontSize="small" />,
      selected: isItalic,
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),
    },
    {
      key: 'underline',
      label: t('teams.panel.underline'),
      icon: <TeamsUnderlineIcon fontSize="small" />,
      selected: isUnderline,
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline'),
    },
    {
      key: 'strikethrough',
      label: t('teams.panel.strikethrough'),
      icon: <TeamsStrikethroughIcon fontSize="small" />,
      selected: isStrike,
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough'),
    },
    { key: 'divider-1', divider: true },
    {
      key: 'bullet',
      label: t('teams.panel.bulletList'),
      icon: <TeamsBulletListIcon fontSize="small" />,
      selected: activeBlock === 'bullet',
      onClick: () => toggleList('bullet'),
    },
    {
      key: 'number',
      label: t('teams.panel.numberedList'),
      icon: <TeamsNumberedListIcon fontSize="small" />,
      selected: activeBlock === 'number',
      onClick: () => toggleList('number'),
    },
    { key: 'divider-2', divider: true },
    {
      key: 'quote',
      label: t('teams.panel.quote'),
      icon: <TeamsQuoteIcon fontSize="small" />,
      selected: activeBlock === 'quote',
      onClick: () => setBlock('quote', $createQuoteNode),
    },
    {
      key: 'link',
      label: t('teams.panel.link'),
      icon: <TeamsLinkIcon fontSize="small" />,
      selected: isLink,
      onClick: (event) => openLinkPopover(event.currentTarget),
    },
    {
      key: 'codeBlock',
      label: t('teams.panel.codeBlock'),
      icon: <TeamsCodeBlockIcon fontSize="small" />,
      selected: activeBlock === 'code',
      onClick: () => setBlock('code', $createCodeNode),
    },
  ];

  /**
   * The compose-box toggle, which is not a formatting control and so does not
   * sit with them: it changes how the box BEHAVES, not what the text looks like.
   * Pushed to the far end for that reason, which is also where Teams puts it.
   */
  const expandLabel = isExpanded
    ? t('teams.panel.composeCollapseAriaLabel')
    : t('teams.panel.composeExpandAriaLabel');

  return (
    <>
      <Box
        role="toolbar"
        aria-label={t('teams.panel.toolbarLabel')}
        sx={{
          display: 'flex',
          // The panel goes as narrow as 280px: the toolbar wraps onto a second
          // row rather than becoming the thing that sets the panel's width.
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.25,
          pb: 0.5,
          minWidth: 0,
        }}
      >
        {/* Collapsed shows nothing but the toggle, which is how Teams behaves:
            formatting is a mode you opt into, and nine controls permanently
            above a one-line box is most of the panel spent on buttons nobody
            is using. Hidden rather than disabled — they are unreachable by
            keyboard too, so tabbing to the message box does not cross them. */}
        {isExpanded &&
          buttons.map((button) =>
          button.divider ? (
            <Divider key={button.key} orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />
          ) : (
            <Tooltip key={button.key} title={button.label}>
              {/* span keeps the tooltip reachable while the button is disabled */}
              <span>
                <ToggleButton
                  value={button.key}
                  size="small"
                  selected={button.selected}
                  disabled={disabled}
                  aria-label={button.label}
                  aria-pressed={button.selected}
                  onClick={(event) => {
                    // Keep the caret in the document: without this the click
                    // collapses the selection the command is meant to act on.
                    event.preventDefault();
                    button.onClick?.(event);
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  sx={{ border: 0, p: 0.5 }}
                >
                  {button.icon}
                </ToggleButton>
              </span>
            </Tooltip>
          ),
        )}

        {onToggleExpanded ? (
          <Tooltip title={expandLabel}>
            <span style={{ marginLeft: 'auto' }}>
              <ToggleButton
                value="expand"
                size="small"
                selected={isExpanded}
                disabled={disabled}
                aria-label={expandLabel}
                aria-pressed={isExpanded}
                onClick={(event) => {
                  event.preventDefault();
                  onToggleExpanded();
                }}
                onMouseDown={(event) => event.preventDefault()}
                sx={{ border: 0, p: 0.5 }}
              >
                <TeamsComposeExpandIcon fontSize="small" />
              </ToggleButton>
            </span>
          </Tooltip>
        ) : null}
      </Box>

      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 260, maxWidth: '90vw' } } }}
      >
        <Stack spacing={1}>
          <TextField
            autoFocus
            size="small"
            label={t('teams.panel.linkUrlLabel')}
            placeholder={t('teams.panel.linkUrlPlaceholder')}
            value={linkUrl}
            error={Boolean(linkError)}
            helperText={linkError ?? ' '}
            onChange={(event) => {
              setLinkUrl(event.target.value);
              setLinkError(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              // The composer's Enter-sends handler must not see this.
              event.preventDefault();
              event.stopPropagation();
              applyLink();
            }}
          />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setLinkAnchor(null)}>
              {t('teams.panel.linkCancel')}
            </Button>
            <Button size="small" variant="contained" onClick={applyLink}>
              {t('teams.panel.linkApply')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

interface ToolbarButton {
  key: string;
  divider?: boolean;
  label?: string;
  icon?: React.ReactNode;
  selected?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}
